/**
 * The shell: sidebar, header, and the screen.
 *
 * FULL WIDTH, NO MAX WIDTH, NO CARD GUTTER. The turn timeline is a horizontal
 * stacked bar whose whole job is letting a reader compare thirty rows against
 * each other, and a centred 78rem column on a 27-inch monitor throws away
 * exactly the axis it is measured on. Prose blocks set their own measure where
 * reading length matters; the tables and the timeline get the width.
 *
 * `min-w-0` ON BOTH THE INSET AND THE CONTENT and it is the non-obvious part: a
 * flex child's default `min-width: auto` means a wide table makes the whole
 * SHELL scroll horizontally instead of scrolling inside its own box, and the
 * sidebar slides off the left of the screen the moment somebody reaches for the
 * last column.
 *
 * ONE BANNER ONLY, and it is the one condition that invalidates everything
 * underneath: the API is unreachable, so every figure on the screen is missing
 * rather than zero. Nothing else earns a banner. The app the shell is borrowed
 * from learnt this the expensive way — a permanent caveat above every screen
 * stops being read as a caveat and starts being read as an accusation. The
 * caveats that matter here are the per-recording ones, and they live next to the
 * recording they are about.
 *
 * "Start a call" LIVES IN THE NAV ONLY. It used to be duplicated into this
 * header (and onto the sessions screen), and both copies were removed on
 * request: the header is now just orientation — trigger, breadcrumb — and the
 * theme toggle stays in the sidebar footer beside the identity block it belongs
 * with. The nav item hides itself for a viewer, whose POST to create a session
 * would be refused with `read_only`.
 */
import type { ReactNode } from 'react'
import { CircleAlertIcon } from 'lucide-react'
import type { PanelUser } from '@/lib/auth'
import { AppSidebar, crumbFor, type NavCounts } from '@/components/app-sidebar'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Separator } from '@/components/ui/separator'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar'
import { TooltipProvider } from '@/components/ui/tooltip'

export function AppShell({
  path,
  counts,
  apiError,
  user,
  onSignOut,
  children,
}: {
  path: string
  counts: NavCounts
  /** Set only when the API itself could not be reached. Not a 404 on one row. */
  apiError: string | null
  /** The signed-in user. The shell is never mounted without one. */
  user: PanelUser | null
  onSignOut: () => void
  children: ReactNode
}) {
  const { group, title } = crumbFor(path)
  return (
    // 300ms rather than 0: the timeline puts five hoverable segments in every
    // row, and an instant tooltip on a bar somebody is sweeping across to READ
    // is a strobe. The segment readout under the timeline is the fast path; the
    // tooltips are for stopping on one.
    <TooltipProvider delayDuration={300}>
      <SidebarProvider>
        <AppSidebar path={path} counts={counts} user={user} onSignOut={onSignOut} />
        <SidebarInset className="min-w-0">
          <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem className="hidden md:block">
                  <span className="text-muted-foreground">{group}</span>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="hidden md:block" />
                <BreadcrumbItem>
                  <BreadcrumbPage>{title}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

          </header>

          {apiError && (
            <div className="px-4 pt-4 md:px-6">
              <Alert variant="destructive">
                <CircleAlertIcon />
                <AlertTitle>Cannot reach the control API</AlertTitle>
                <AlertDescription>
                  {apiError}
                  {' — every figure below is missing rather than zero. Live calls are '}
                  unaffected: media is browser-to-OpenAI and this app is not in that path.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* min-w-0 again, for the reason at the top of the file. */}
          <div className="min-w-0 flex-1 p-4 md:p-6">{children}</div>
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  )
}
